import { useState, useRef, useEffect } from "react";

// ─── Data ─────────────────────────────────────────────────────────────────────

// 12 positions in circle-of-fifths order
const KEYS = [
  { major:"C",  minor:"Am",  rel:"Am",  sharps:0  },
  { major:"G",  minor:"Em",  rel:"Em",  sharps:1  },
  { major:"D",  minor:"Bm",  rel:"Bm",  sharps:2  },
  { major:"A",  minor:"F#m", rel:"F#m", sharps:3  },
  { major:"E",  minor:"C#m", rel:"C#m", sharps:4  },
  { major:"B",  minor:"G#m", rel:"G#m", sharps:5  },
  { major:"F#", minor:"D#m", rel:"D#m", sharps:6  },
  { major:"Db", minor:"Bbm", rel:"Bbm", sharps:-5 },
  { major:"Ab", minor:"Fm",  rel:"Fm",  sharps:-4 },
  { major:"Eb", minor:"Cm",  rel:"Cm",  sharps:-3 },
  { major:"Bb", minor:"Gm",  rel:"Gm",  sharps:-2 },
  { major:"F",  minor:"Dm",  rel:"Dm",  sharps:-1 },
];

// Dominant 7th for each major key (V7 chord)
const DOM7 = ["G7","D7","A7","E7","B7","F#7","C#7","Ab7","Eb7","Bb7","F7","C7"];

// Diminished chord (VII° of each major key)
const DIM  = ["B°","F#°","C#°","G#°","D#°","A#°","E#°","A°","E°","B°","F°","E°"];
// simplified (enharmonic):
const DIM2 = ["B°","F#°","C#°","G#°","D#°","Bb°","F°","Ab°","Eb°","Bb°","F°","E°"];

// Notes in each major triad (root position) — for the detail panel
const NOTES_SHARP = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const NOTES_FLAT  = ["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"];

function pickArr(sharps) { return sharps >= 0 ? NOTES_SHARP : NOTES_FLAT; }
function noteIdx(n) { const a = NOTES_SHARP.indexOf(n); return a >= 0 ? a : NOTES_FLAT.indexOf(n); }
function majorTriad(root, sharps) {
  const arr = pickArr(sharps);
  let ri = arr.indexOf(root);
  if (ri < 0) { const alt = sharps>=0?NOTES_FLAT:NOTES_SHARP; ri=alt.indexOf(root); if(ri<0) return [root]; return [alt[ri],alt[(ri+4)%12],alt[(ri+7)%12]]; }
  return [arr[ri], arr[(ri+4)%12], arr[(ri+7)%12]];
}
function minorTriad(root, sharps) {
  const arr = pickArr(sharps);
  const rr  = root.replace("m","");
  let ri = arr.indexOf(rr);
  if (ri < 0) { const alt = sharps>=0?NOTES_FLAT:NOTES_SHARP; ri=alt.indexOf(rr); if(ri<0) return [rr]; return [alt[ri],alt[(ri+3)%12],alt[(ri+7)%12]]; }
  return [arr[ri], arr[(ri+3)%12], arr[(ri+7)%12]];
}
function dom7Notes(root, sharps) {
  const rr  = root.replace("7","");
  const arr = pickArr(sharps);
  let ri = arr.indexOf(rr);
  if (ri < 0) { const alt = sharps>=0?NOTES_FLAT:NOTES_SHARP; ri=alt.indexOf(rr); if(ri<0) return [rr]; return [alt[ri],alt[(ri+4)%12],alt[(ri+7)%12],alt[(ri+10)%12]]; }
  return [arr[ri], arr[(ri+4)%12], arr[(ri+7)%12], arr[(ri+10)%12]];
}

// ─── Node catalogue ───────────────────────────────────────────────────────────

// Build the full node list with ring/position info
// Ring 0 = major (outer), Ring 1 = minor, Ring 2 = dom7, Ring 3 = dim (inner)
function buildNodes(CX, CY, R) {
  const nodes = [];
  const n = KEYS.length;

  KEYS.forEach((k, i) => {
    const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const sharps = k.sharps;

    // Outer ring: major
    nodes.push({
      id: `maj-${i}`, ring: 0, keyIdx: i,
      label: k.major, type: "maj", sharps,
      x: CX + R[0]*cos, y: CY + R[0]*sin, angle,
      notes: majorTriad(k.major, sharps),
      desc: `${k.major}-Dur`,
    });

    // Second ring: minor (offset by half-step so they sit between the majors)
    const mAngle = angle + (Math.PI / n);
    nodes.push({
      id: `min-${i}`, ring: 1, keyIdx: i,
      label: k.minor, type: "min", sharps,
      x: CX + R[1]*Math.cos(mAngle), y: CY + R[1]*Math.sin(mAngle), angle: mAngle,
      notes: minorTriad(k.minor, sharps),
      desc: `${k.minor} (Relative Moll von ${k.major}-Dur)`,
    });

    // Third ring: dominant 7th
    nodes.push({
      id: `dom-${i}`, ring: 2, keyIdx: i,
      label: DOM7[i], type: "dom7", sharps,
      x: CX + R[2]*cos, y: CY + R[2]*sin, angle,
      notes: dom7Notes(DOM7[i], sharps),
      desc: `${DOM7[i]} → löst nach ${k.major} auf (V7→I)`,
    });

    // Inner ring: diminished
    nodes.push({
      id: `dim-${i}`, ring: 3, keyIdx: i,
      label: DIM2[i], type: "dim", sharps,
      x: CX + R[3]*cos, y: CY + R[3]*sin, angle,
      notes: [], // optional
      desc: `${DIM2[i]} — Leittonakkord von ${k.major}`,
    });
  });

  return nodes;
}

// ─── Connection rules ─────────────────────────────────────────────────────────

// Returns list of [fromId, toId, kind] connections for a given node
function getConnections(node, nodes) {
  const conn = [];
  const i    = node.keyIdx;
  const n    = KEYS.length;
  const prev = (i + n - 1) % n;
  const next = (i + 1) % n;

  if (node.type === "maj") {
    // V7 → I  (dom7 of same key resolves here)
    conn.push({ fromId:`dom-${i}`, toId:`maj-${i}`, kind:"resolve" });
    // Adjacent circle of fifths
    conn.push({ fromId:`maj-${i}`, toId:`maj-${next}`, kind:"fifth" });
    conn.push({ fromId:`maj-${prev}`, toId:`maj-${i}`, kind:"fifth" });
    // Relative minor
    conn.push({ fromId:`maj-${i}`, toId:`min-${i}`, kind:"relative" });
    // Parallel minor (same root, minor) — find minor with same note
    const parIdx = KEYS.findIndex(k => k.minor.replace("m","") === KEYS[i].major);
    if (parIdx >= 0) conn.push({ fromId:`maj-${i}`, toId:`min-${parIdx}`, kind:"parallel" });
  }

  if (node.type === "min") {
    // Relative major
    conn.push({ fromId:`min-${i}`, toId:`maj-${i}`, kind:"relative" });
    // ii → V: minor is ii of key one step counter-clockwise
    conn.push({ fromId:`min-${i}`, toId:`dom-${prev}`, kind:"twoFive" });
    // Adjacent minor
    conn.push({ fromId:`min-${i}`, toId:`min-${next}`, kind:"fifth" });
  }

  if (node.type === "dom7") {
    // V7 → I
    conn.push({ fromId:`dom-${i}`, toId:`maj-${i}`, kind:"resolve" });
    // tritone sub: dom7 can also resolve to the key 6 steps away
    const tri = (i + 6) % n;
    conn.push({ fromId:`dom-${i}`, toId:`maj-${tri}`, kind:"tritone" });
    // ii→V: comes from the minor a step clockwise
    conn.push({ fromId:`min-${next}`, toId:`dom-${i}`, kind:"twoFive" });
  }

  if (node.type === "dim") {
    // dim → resolves to major (leading tone function)
    conn.push({ fromId:`dim-${i}`, toId:`maj-${i}`, kind:"resolve" });
    // dim can also go to minor
    conn.push({ fromId:`dim-${i}`, toId:`min-${i}`, kind:"resolve" });
  }

  return conn;
}

// ─── Style maps ───────────────────────────────────────────────────────────────

const RING_STYLE = {
  maj:  { fill:"#1a1a1a", stroke:"#444",    text:"#fff",   r:20 },
  min:  { fill:"#3b5e8c", stroke:"#5b7eac", text:"#fff",   r:16 },
  dom7: { fill:"#7c4a00", stroke:"#c67c00", text:"#ffe082", r:14 },
  dim:  { fill:"#4a1060", stroke:"#8e24aa", text:"#e1bee7", r:12 },
};

const CONN_STYLE = {
  resolve:  { stroke:"#81c784", sw:2,   dash:undefined },
  fifth:    { stroke:"#64b5f6", sw:1.5, dash:undefined },
  relative: { stroke:"#ce93d8", sw:1.5, dash:"5,3"    },
  parallel: { stroke:"#ffcc80", sw:1,   dash:"3,4"    },
  twoFive:  { stroke:"#ef9a9a", sw:1.5, dash:undefined },
  tritone:  { stroke:"#ff8a65", sw:1,   dash:"2,4"    },
};

const KIND_LABEL = {
  resolve:  "V7→I Auflösung",
  fifth:    "Quintschritt",
  relative: "Relative Dur/Moll",
  parallel: "Paralleltonart",
  twoFive:  "II–V Bewegung",
  tritone:  "Tritonussubstitution",
};

// ─── Arrow helper ─────────────────────────────────────────────────────────────

function arrow(from, to, rFrom, rTo, CX, CY) {
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.sqrt(dx*dx+dy*dy) || 1;
  const ux = dx/len, uy = dy/len;
  const sx = from.x + ux*rFrom, sy = from.y + uy*rFrom;
  const ex = to.x   - ux*(rTo+5), ey = to.y - uy*(rTo+5);
  // Slight curve toward center
  const mx = (sx+ex)/2, my = (sy+ey)/2;
  const ocx = CX-mx, ocy = CY-my;
  const ol  = Math.sqrt(ocx*ocx+ocy*ocy)||1;
  const cpx = mx + (ocx/ol)*18, cpy = my + (ocy/ol)*18;
  return `M${sx.toFixed(1)},${sy.toFixed(1)} Q${cpx.toFixed(1)},${cpy.toFixed(1)} ${ex.toFixed(1)},${ey.toFixed(1)}`;
}

// ─── Mini Piano (compact) ────────────────────────────────────────────────────

const SEMI_TO_WPOS   = [0,0,1,1,2,3,3,4,4,5,5,6];
const WPOS_TO_SEMI   = [0,2,4,5,7,9,11];
const WHITE_HAS_BLK  = [true,true,false,true,true,true,false];

function semiToAbsW(s) { return Math.floor(s/12)*7 + SEMI_TO_WPOS[s%12]; }

function MiniPiano({ notes, color="#555" }) {
  if (!notes||notes.length===0) return null;
  const semis = notes.map(n => noteIdx(n)).filter(x=>x>=0);
  if (!semis.length) return null;
  const lo = semis[0], hi = semis[semis.length-1];
  const sw = semiToAbsW(lo), lw = semiToAbsW(hi);
  const ew = Math.max(lw+1, sw+4), nw = ew-sw+1;
  const ww=12, wh=26, bw=7, bh=15, totalW=nw*ww;
  const act = new Set(semis.map(s=>s%12));
  const wkeys=[];
  for(let w=sw;w<=ew;w++){
    const oct=Math.floor(w/7), wInOct=w%7;
    const abs=oct*12+WPOS_TO_SEMI[wInOct];
    wkeys.push({abs, sioc:abs%12, xPos:(w-sw)*ww, wInOct});
  }
  return (
    <svg width={totalW} height={wh} style={{display:"block",borderRadius:2}}>
      {wkeys.map((k,i)=><rect key={"w"+i} x={k.xPos} y={0} width={ww-1} height={wh} rx={1} fill={act.has(k.sioc)?color:"#fff"} stroke="#ccc" strokeWidth={0.5}/>)}
      {wkeys.map((k,i)=>{
        if(!WHITE_HAS_BLK[k.wInOct])return null;
        const bs=(k.sioc+1)%12;
        const bx=k.xPos+ww-bw/2;
        if(bx+bw>totalW)return null;
        return<rect key={"b"+i} x={bx} y={0} width={bw} height={bh} rx={1} fill={act.has(bs)?color:"#222"}/>;
      })}
    </svg>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:"6px 12px", padding:"10px 12px", borderRadius:10, background:"#111", border:"1px solid #333", marginBottom:10 }}>
      {Object.entries(KIND_LABEL).map(([k,label]) => {
        const s = CONN_STYLE[k];
        return (
          <div key={k} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <svg width={24} height={8}>
              <line x1={0} y1={4} x2={24} y2={4} stroke={s.stroke} strokeWidth={s.sw} strokeDasharray={s.dash}/>
            </svg>
            <span style={{ fontSize:9, color:"#999", fontFamily:"Inter,sans-serif" }}>{label}</span>
          </div>
        );
      })}
      <div style={{ display:"flex", alignItems:"center", gap:8, width:"100%", marginTop:2 }}>
        {Object.entries(RING_STYLE).map(([type,st]) => (
          <div key={type} style={{ display:"flex", alignItems:"center", gap:4 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:st.fill, border:`1px solid ${st.stroke}` }}/>
            <span style={{ fontSize:9, color:"#888", fontFamily:"Inter,sans-serif" }}>
              {type==="maj"?"Dur":type==="min"?"Moll":type==="dom7"?"Dom7":"Verm°"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HarmonyMap() {
  const containerRef = useRef(null);
  const [W, setW]         = useState(360);
  const [selId, setSelId] = useState(null);
  const [showLegend, setShowLegend] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    obs.observe(containerRef.current);
    setW(Math.floor(containerRef.current.offsetWidth)||360);
    return () => obs.disconnect();
  }, []);

  const CX = W / 2;
  const maxR = Math.min(W / 2 - 24, 210);
  const R = [maxR, maxR*0.73, maxR*0.50, maxR*0.30];
  const H = maxR * 2 + 48;

  const CY = H / 2;

  const nodes = buildNodes(CX, CY, R);
  const byId  = Object.fromEntries(nodes.map(n => [n.id, n]));

  const selNode = selId ? byId[selId] : null;
  const activeConns = selNode ? getConnections(selNode, nodes) : [];

  // Collect all involved node IDs when something is selected
  const activeIds = new Set(activeConns.flatMap(c => [c.fromId, c.toId]));
  if (selId) activeIds.add(selId);

  // Build all arrow paths to render (only for selected node's connections)
  // When nothing selected, show a curated static set (circle of fifths major ring only)
  const defaultConns = selId ? [] : KEYS.flatMap((_,i) => {
    const next = (i+1)%12;
    return [
      { fromId:`maj-${i}`, toId:`maj-${next}`, kind:"fifth" },
      { fromId:`dom-${i}`, toId:`maj-${i}`,    kind:"resolve" },
      { fromId:`min-${i}`, toId:`maj-${i}`,    kind:"relative" },
    ];
  });

  const connsToRender = selId ? activeConns : defaultConns;

  return (
    <div ref={containerRef} style={{ width:"100%", background:"#0d0d0d", borderRadius:14, padding:"12px 0 0" }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 14px", marginBottom:8 }}>
        <div>
          <div style={{ fontFamily:"Inter,sans-serif", fontSize:16, fontWeight:700, color:"#fff", letterSpacing:"-0.01em" }}>Harmony Map</div>
          <div style={{ fontSize:10, color:"#555", fontFamily:"Inter,sans-serif", marginTop:1 }}>
            {selId ? "Verbindungen des gewählten Akkords" : "Akkord antippen für Details"}
          </div>
        </div>
        <button onClick={() => setShowLegend(v=>!v)} style={{
          padding:"4px 10px", borderRadius:8, border:"1px solid #333",
          background: showLegend ? "#333" : "transparent",
          color:"#888", fontFamily:"Inter,sans-serif", fontSize:10, fontWeight:600, cursor:"pointer",
        }}>Legende</button>
      </div>

      {showLegend && <div style={{ padding:"0 12px 8px" }}><Legend /></div>}

      {/* SVG */}
      <div style={{ overflowX:"auto" }}>
        <svg width={W} height={H} style={{ display:"block" }}>
          <defs>
            {["resolve","fifth","relative","parallel","twoFive","tritone"].map(k => (
              <marker key={k} id={`hm-${k}`} markerWidth="6" markerHeight="5" refX="5.5" refY="2.5" orient="auto">
                <polygon points="0,0 6,2.5 0,5" fill={CONN_STYLE[k].stroke} opacity={0.9}/>
              </marker>
            ))}
          </defs>

          {/* Subtle ring guides */}
          {R.map((r,i) => (
            <circle key={i} cx={CX} cy={CY} r={r} fill="none" stroke="#1e1e1e" strokeWidth={1}/>
          ))}

          {/* Connections */}
          {connsToRender.map((c,i) => {
            const from = byId[c.fromId], to = byId[c.toId];
            if (!from||!to) return null;
            const st   = CONN_STYLE[c.kind];
            const rF   = RING_STYLE[from.type].r;
            const rT   = RING_STYLE[to.type].r;
            const faded = selId && !activeConns.some(ac=>ac.fromId===c.fromId&&ac.toId===c.toId&&ac.kind===c.kind);
            return (
              <path key={i}
                d={arrow(from, to, rF, rT, CX, CY)}
                fill="none"
                stroke={faded ? "#222" : st.stroke}
                strokeWidth={faded ? 0.5 : st.sw}
                strokeDasharray={st.dash}
                opacity={faded ? 0.4 : 0.85}
                markerEnd={`url(#hm-${c.kind})`}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map(node => {
            const st     = RING_STYLE[node.type];
            const isSel  = node.id === selId;
            const isConn = activeIds.has(node.id);
            const faded  = selId && !isConn;
            return (
              <g key={node.id}
                onClick={() => setSelId(selId===node.id ? null : node.id)}
                style={{ cursor:"pointer" }}>
                <circle
                  cx={node.x} cy={node.y} r={st.r}
                  fill={faded ? "#111" : isSel ? "#fff" : st.fill}
                  stroke={faded ? "#222" : isSel ? "#fff" : st.stroke}
                  strokeWidth={isSel ? 2.5 : 1.5}
                  opacity={faded ? 0.35 : 1}
                />
                <text
                  x={node.x} y={node.y} textAnchor="middle" dominantBaseline="middle"
                  fontSize={node.label.length>=4 ? st.r*0.52 : st.r*0.62}
                  fontWeight="700" fontFamily="Inter,sans-serif"
                  fill={faded ? "#333" : isSel ? st.fill : st.text}
                  style={{ pointerEvents:"none", userSelect:"none" }}>
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Detail panel */}
      {selNode && (
        <div style={{ margin:"8px 12px 12px", borderRadius:12, background:"#181818", border:"1px solid #2a2a2a", padding:"12px 14px" }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
            <div>
              <div style={{ fontFamily:"Inter,sans-serif", fontSize:18, fontWeight:700, color:"#fff", letterSpacing:"-0.01em" }}>
                {selNode.label}
              </div>
              <div style={{ fontSize:10, color:"#666", fontFamily:"Inter,sans-serif", marginTop:2 }}>
                {selNode.desc}
              </div>
            </div>
            <button onClick={() => setSelId(null)} style={{
              padding:"3px 10px", borderRadius:8, border:"1px solid #333",
              background:"transparent", color:"#666", fontFamily:"Inter,sans-serif", fontSize:11, cursor:"pointer",
            }}>✕</button>
          </div>

          {/* Notes */}
          {selNode.notes.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:9, color:"#555", fontFamily:"Inter,sans-serif", fontWeight:700, letterSpacing:"0.06em", marginBottom:5 }}>TÖNE</div>
              <div style={{ display:"flex", gap:5, alignItems:"center", marginBottom:6 }}>
                {selNode.notes.map((n,i) => (
                  <span key={i} style={{
                    fontSize:12, fontWeight:700, color:"#fff",
                    background:"#222", border:"1px solid #333",
                    borderRadius:6, padding:"3px 8px",
                    fontFamily:"Inter,sans-serif",
                  }}>{n}</span>
                ))}
              </div>
              <MiniPiano notes={selNode.notes} color="#6FAE8D"/>
            </div>
          )}

          {/* Connections legend */}
          {activeConns.length > 0 && (
            <div>
              <div style={{ fontSize:9, color:"#555", fontFamily:"Inter,sans-serif", fontWeight:700, letterSpacing:"0.06em", marginBottom:6 }}>VERBINDUNGEN</div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {activeConns.map((c,i) => {
                  const from = byId[c.fromId], to = byId[c.toId];
                  if (!from||!to) return null;
                  const st = CONN_STYLE[c.kind];
                  const isFrom = c.fromId === selId;
                  return (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <svg width={20} height={8} style={{flexShrink:0}}>
                        <line x1={0} y1={4} x2={20} y2={4} stroke={st.stroke} strokeWidth={st.sw} strokeDasharray={st.dash}/>
                      </svg>
                      <span style={{ fontSize:10, color:"#777", fontFamily:"Inter,sans-serif" }}>
                        <span style={{color:"#aaa", fontWeight:600}}>
                          {isFrom ? to.label : from.label}
                        </span>
                        {" · "}{KIND_LABEL[c.kind]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
